// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * ARCHAT — Registro de suscripción push (PWA)
 * Requiere VITE_VAPID_PUBLIC_KEY en build (clave pública VAPID base64url).
 * Si no hay clave, no se llama a subscribe (no rompe la app).
 */

import { request } from '../services/apiClient';
import { API_BASE } from '../utils/constants';
import { getNotificationPermissionState, requestNotificationPermission } from '../utils/NotificationPermission';

/**
 * Convierte clave VAPID pública base64url a Uint8Array para PushManager.
 * @param {string} base64url
 * @returns {Uint8Array|null}
 */
function urlBase64ToUint8Array(base64url) {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  try {
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) {
      out[i] = raw.charCodeAt(i);
    }
    return out;
  } catch (_) {
    return null;
  }
}

/**
 * Registra la suscripción push con el backend (requiere usuario autenticado).
 * Si no hay Service Worker, VAPID key o el usuario no está logueado, no hace nada.
 */
export async function registerPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return;
  }

  const vapidPublic = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (!vapidPublic || vapidPublic === 'PUBLIC_VAPID_KEY') {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[ARCHAT] Push: VAPID key not set, skip subscription');
    }
    return;
  }

  const applicationServerKey = urlBase64ToUint8Array(vapidPublic);
  if (!applicationServerKey) {
    return;
  }

  const perm = getNotificationPermissionState();
  if (perm === 'denied' || perm === 'unsupported') {
    return;
  }
  if (perm === 'default') {
    const next = await requestNotificationPermission();
    if (next !== 'granted') {
      return;
    }
  }

  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey
    });

    const payload = sub.toJSON ? sub.toJSON() : null;
    if (!payload || !payload.endpoint || !payload.keys) {
      return;
    }

    const base = (API_BASE && API_BASE.startsWith('http'))
      ? API_BASE.replace(/\/$/, '')
      : (typeof window !== 'undefined' ? (window.location.origin + (API_BASE.startsWith('/') ? API_BASE : '/' + API_BASE)) : API_BASE).replace(/\/$/, '');
    const url = base + '/push/subscribe.php';

    const res = await request(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!res.ok && res.status === 401) {
      return;
    }
    if (!res.ok && typeof console !== 'undefined' && console.warn) {
      console.warn('[ARCHAT] Push subscribe failed', res.status);
    }
  } catch (err) {
    if (err && err.status === 401) {
      return;
    }
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[ARCHAT] Push register error', err);
    }
  }
}
